/**
 * Insurance Admin Dashboard
 * Complete management panel for insurance module with verification queue,
 * provider management, compliance review, and settings.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Separator } from '../../../components/ui/separator';
import { Progress } from '../../../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { useToast } from '../../../hooks/use-toast';
import {
  Shield,
  Building2,
  Users,
  FileText,
  Settings,
  CheckCircle2,
  XCircle,
  Clock,
  Star,
  TrendingUp,
  ArrowLeft,
  Loader2,
  BarChart3,
  AlertTriangle,
  DollarSign,
  Bell,
  Eye,
  RefreshCw,
  BadgeCheck,
  ShieldAlert,
  FileCheck,
  Globe,
  Phone,
  Mail,
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Filter,
  PiggyBank,
  Receipt
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InsuranceAdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('providers');
  
  // Data states
  const [config, setConfig] = useState({});
  const [providers, setProviders] = useState([]);
  const [verificationQueue, setVerificationQueue] = useState([]);
  const [categories, setCategories] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [referrals, setReferrals] = useState([]);
  
  // Dialog states
  const [selectedInsurer, setSelectedInsurer] = useState(null);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [verificationAction, setVerificationAction] = useState('');
  const [verificationNotes, setVerificationNotes] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      const [configRes, providersRes, queueRes, categoriesRes, analyticsRes, referralsRes] = await Promise.all([
        axios.get(`${API_URL}/api/insurance/admin/config`),
        axios.get(`${API_URL}/api/insurance/admin/providers`),
        axios.get(`${API_URL}/api/insurance/admin/verification-queue`),
        axios.get(`${API_URL}/api/insurance/categories`),
        axios.get(`${API_URL}/api/insurance/admin/analytics`),
        axios.get(`${API_URL}/api/insurance/admin/referrals`)
      ]);
      
      setConfig(configRes.data.config || {});
      setProviders(providersRes.data.providers || []);
      setVerificationQueue(queueRes.data.queue || []);
      setCategories(categoriesRes.data.categories || []);
      setAnalytics(analyticsRes.data.analytics || {});
      setReferrals(referralsRes.data.referrals || []);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({ title: "Error loading dashboard", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      await axios.post(`${API_URL}/api/insurance/admin/config`, config, {
        params: { admin_id: 'admin' }
      });
      toast({ title: "Configuration saved" });
    } catch (error) {
      toast({ title: "Failed to save config", variant: "destructive" });
    }
  };

  const handleVerificationAction = async () => {
    if (!selectedInsurer || !verificationAction) return;
    
    try {
      setProcessingAction(true);
      
      await axios.post(
        `${API_URL}/api/insurance/admin/verify-insurer/${selectedInsurer.id}`,
        {
          action: verificationAction,
          notes: verificationNotes
        },
        { params: { admin_id: 'admin' } }
      );
      
      toast({ title: `Insurer ${verificationAction}ed successfully` });
      setShowVerificationDialog(false);
      setSelectedInsurer(null);
      setVerificationAction('');
      setVerificationNotes('');
      fetchAllData();
      
    } catch (error) {
      toast({ title: "Action failed", variant: "destructive" });
    } finally {
      setProcessingAction(false);
    }
  };

  const openVerificationDialog = (insurer) => {
    setSelectedInsurer(insurer);
    setShowVerificationDialog(true);
  };

  const getStatusBadge = (status) => {
    const config = {
      pending_review: { color: 'bg-yellow-100 text-yellow-700', label: 'Pending Review' },
      active: { color: 'bg-green-100 text-green-700', label: 'Active' },
      rejected: { color: 'bg-red-100 text-red-700', label: 'Rejected' },
      suspended: { color: 'bg-gray-100 text-gray-700', label: 'Suspended' },
      info_requested: { color: 'bg-blue-100 text-blue-700', label: 'Info Requested' }
    };
    const c = config[status] || { color: 'bg-gray-100 text-gray-700', label: status };
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getRiskBadge = (riskLevel) => {
    const config = {
      very_low: { color: 'bg-green-100 text-green-700', label: 'Very Low Risk' },
      low: { color: 'bg-emerald-100 text-emerald-700', label: 'Low Risk' },
      medium: { color: 'bg-yellow-100 text-yellow-700', label: 'Medium Risk' },
      high: { color: 'bg-red-100 text-red-700', label: 'High Risk' }
    };
    const c = config[riskLevel] || { color: 'bg-gray-100 text-gray-700', label: riskLevel };
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getRecommendationBadge = (rec) => {
    const config = {
      APPROVE: { color: 'bg-green-500 text-white', label: 'Auto-Approve' },
      MANUAL_REVIEW: { color: 'bg-yellow-500 text-white', label: 'Manual Review' },
      REJECT: { color: 'bg-red-500 text-white', label: 'Reject' }
    };
    const c = config[rec] || { color: 'bg-gray-500 text-white', label: rec };
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-2">
                <Shield className="h-6 w-6 text-indigo-600" />
                <h1 className="text-xl font-bold text-gray-900">Insurance Admin</h1>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchAllData}>
                <RefreshCw className="h-4 w-4 mr-1" /> Refresh
              </Button>
              <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-lg">
                <span className="text-sm text-gray-600">Module:</span>
                <Switch
                  checked={config.module_enabled}
                  onCheckedChange={(v) => setConfig(prev => ({ ...prev, module_enabled: v }))}
                />
                <span className={`text-sm font-medium ${config.module_enabled ? 'text-green-600' : 'text-gray-400'}`}>
                  {config.module_enabled ? 'ON' : 'OFF'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Building2 className="h-8 w-8 text-indigo-500" />
                <div>
                  <p className="text-sm text-gray-500">Active Providers</p>
                  <p className="text-2xl font-bold">{analytics.active_providers || providers.filter(p => p.is_active).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <ShieldAlert className="h-8 w-8 text-yellow-600" />
                <div>
                  <p className="text-sm text-yellow-700">Pending Verification</p>
                  <p className="text-2xl font-bold text-yellow-800">{verificationQueue.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Users className="h-8 w-8 text-blue-500" />
                <div>
                  <p className="text-sm text-gray-500">Total Leads</p>
                  <p className="text-2xl font-bold">{analytics.total_leads || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-8 w-8 text-green-500" />
                <div>
                  <p className="text-sm text-gray-500">Policies Issued</p>
                  <p className="text-2xl font-bold">{analytics.policies_issued || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <DollarSign className="h-8 w-8 text-emerald-500" />
                <div>
                  <p className="text-sm text-gray-500">Revenue</p>
                  <p className="text-2xl font-bold">${analytics.total_revenue || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-6 w-full">
            <TabsTrigger value="providers">
              <Building2 className="h-4 w-4 mr-1" /> Providers
            </TabsTrigger>
            <TabsTrigger value="verification" className="relative">
              <ShieldAlert className="h-4 w-4 mr-1" /> Verification
              {verificationQueue.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {verificationQueue.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="categories">
              <Filter className="h-4 w-4 mr-1" /> Categories
            </TabsTrigger>
            <TabsTrigger value="monetization">
              <PiggyBank className="h-4 w-4 mr-1" /> Monetization
            </TabsTrigger>
            <TabsTrigger value="referrals">
              <Receipt className="h-4 w-4 mr-1" /> Leads
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="h-4 w-4 mr-1" /> Settings
            </TabsTrigger>
          </TabsList>

          {/* Providers Tab */}
          <TabsContent value="providers">
            <Card>
              <CardHeader>
                <CardTitle>Insurance Providers</CardTitle>
                <CardDescription>Manage active insurance providers</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Provider</TableHead>
                      <TableHead>Country</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Badges</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Trust Score</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {providers.map(provider => (
                      <TableRow key={provider.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {provider.is_sponsored && <Star className="h-4 w-4 text-yellow-500" />}
                            <div>
                              <p className="font-medium">{provider.name}</p>
                              {provider.trading_name && (
                                <p className="text-xs text-gray-500">Trading as: {provider.trading_name}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{provider.countries?.join(', ')}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{provider.provider_type || 'insurance_company'}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {provider.badges?.slice(0, 2).map(badge => (
                              <Badge key={badge} variant="secondary" className="text-xs">{badge}</Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          {provider.is_active ? (
                            <Badge className="bg-green-100 text-green-700">Active</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-700">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{provider.trust_score || 0}</span>
                            <Progress value={provider.trust_score || 0} className="w-16 h-2" />
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {providers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                          No providers found. Demo providers are shown to users automatically.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Verification Queue Tab */}
          <TabsContent value="verification">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5 text-yellow-600" />
                  Verification Queue
                </CardTitle>
                <CardDescription>Review and approve new insurer registrations</CardDescription>
              </CardHeader>
              <CardContent>
                {verificationQueue.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-300" />
                    <p className="text-lg font-medium text-gray-600">No pending verifications</p>
                    <p className="text-sm">All insurer registrations have been reviewed.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {verificationQueue.map(insurer => (
                      <div
                        key={insurer.id}
                        className="p-4 border rounded-lg hover:border-indigo-300 transition-all"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-semibold text-lg">{insurer.legal_name}</h4>
                              {getStatusBadge(insurer.status)}
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                              <div>
                                <span className="text-gray-500">Country:</span>
                                <p className="font-medium">{insurer.country}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Email:</span>
                                <p className="font-medium">{insurer.email}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Phone:</span>
                                <p className="font-medium">{insurer.phone}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Website:</span>
                                <p className="font-medium">
                                  {insurer.website_url ? (
                                    <a href={insurer.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                                      Visit <ExternalLink className="h-3 w-3" />
                                    </a>
                                  ) : 'Not provided'}
                                </p>
                              </div>
                            </div>
                            
                            {/* Verification Summary */}
                            {insurer.verification && (
                              <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                                <div className="text-center">
                                  <p className={`text-3xl font-bold ${
                                    insurer.verification.risk_score >= 80 ? 'text-green-600' :
                                    insurer.verification.risk_score >= 55 ? 'text-yellow-600' : 'text-red-600'
                                  }`}>
                                    {insurer.verification.risk_score}/100
                                  </p>
                                  <p className="text-xs text-gray-500">Verification Score</p>
                                </div>
                                <Separator orientation="vertical" className="h-12" />
                                <div>
                                  {getRiskBadge(insurer.verification.risk_level)}
                                  <div className="mt-1">
                                    {getRecommendationBadge(insurer.verification.recommendation)}
                                  </div>
                                </div>
                                <Separator orientation="vertical" className="h-12" />
                                <div className="flex-1">
                                  <p className="text-sm text-gray-600">{insurer.verification.ai_summary}</p>
                                </div>
                              </div>
                            )}
                            
                            {/* Risk Flags */}
                            {insurer.verification?.flags?.length > 0 && (
                              <div className="mt-3">
                                <p className="text-sm font-medium text-red-600 mb-1">⚠️ Risk Flags:</p>
                                <div className="flex flex-wrap gap-2">
                                  {insurer.verification.flags.map((flag, idx) => (
                                    <Badge key={idx} variant="outline" className="text-red-600 border-red-200">
                                      {flag.message}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex flex-col gap-2 ml-4">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => {
                                setSelectedInsurer(insurer);
                                setVerificationAction('approve');
                                setShowVerificationDialog(true);
                              }}
                            >
                              <ThumbsUp className="h-4 w-4 mr-1" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedInsurer(insurer);
                                setVerificationAction('request_info');
                                setShowVerificationDialog(true);
                              }}
                            >
                              <MessageSquare className="h-4 w-4 mr-1" /> Request Info
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => {
                                setSelectedInsurer(insurer);
                                setVerificationAction('reject');
                                setShowVerificationDialog(true);
                              }}
                            >
                              <ThumbsDown className="h-4 w-4 mr-1" /> Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <Card>
              <CardHeader>
                <CardTitle>Insurance Categories / Badges</CardTitle>
                <CardDescription>Manage filter badges shown to buyers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categories.map(cat => (
                    <div key={cat.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge className={`bg-${cat.color}-100 text-${cat.color}-700`}>{cat.name}</Badge>
                        </div>
                        <Switch checked={cat.enabled} />
                      </div>
                      <p className="text-sm text-gray-600">{cat.description}</p>
                      <p className="text-xs text-gray-400 mt-2">Filter: {cat.filter_key} = {String(cat.filter_value)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Monetization Tab */}
          <TabsContent value="monetization">
            <div className="space-y-6">
              {/* Revenue Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-r from-blue-50 to-indigo-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <PiggyBank className="h-8 w-8 text-blue-600" />
                      <div>
                        <p className="text-sm text-blue-700">Lead Revenue</p>
                        <p className="text-2xl font-bold text-blue-800">${analytics.lead_revenue || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-r from-green-50 to-emerald-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="text-sm text-green-700">Quote Revenue</p>
                        <p className="text-2xl font-bold text-green-800">${analytics.quote_revenue || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-r from-purple-50 to-violet-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Star className="h-8 w-8 text-purple-600" />
                      <div>
                        <p className="text-sm text-purple-700">Sponsored Slots</p>
                        <p className="text-2xl font-bold text-purple-800">
                          {providers.filter(p => p.is_sponsored).length} / {config.sponsored_placements_max || 2}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-r from-emerald-50 to-teal-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <DollarSign className="h-8 w-8 text-emerald-600" />
                      <div>
                        <p className="text-sm text-emerald-700">Total Revenue</p>
                        <p className="text-2xl font-bold text-emerald-800">${analytics.total_revenue || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Commission Settings */}
              <Card>
                <CardHeader>
                  <CardTitle>Default Commission Rates</CardTitle>
                  <CardDescription>Set default rates for new providers</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Lead Price ($)</Label>
                      <Input
                        type="number"
                        value={config.default_lead_price || 25}
                        onChange={(e) => setConfig(prev => ({ ...prev, default_lead_price: parseFloat(e.target.value) }))}
                      />
                      <p className="text-xs text-gray-500 mt-1">Charged per quote request</p>
                    </div>
                    <div>
                      <Label>Quote Price ($)</Label>
                      <Input
                        type="number"
                        value={config.default_quote_price || 5}
                        onChange={(e) => setConfig(prev => ({ ...prev, default_quote_price: parseFloat(e.target.value) }))}
                      />
                      <p className="text-xs text-gray-500 mt-1">Charged when quote is sent</p>
                    </div>
                    <div>
                      <Label>Policy Commission (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={config.default_policy_commission_percent || 2}
                        onChange={(e) => setConfig(prev => ({ ...prev, default_policy_commission_percent: parseFloat(e.target.value) }))}
                      />
                      <p className="text-xs text-gray-500 mt-1">% of premium on policy issue</p>
                    </div>
                  </div>
                  <Button onClick={handleSaveConfig}>Save Commission Settings</Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Leads/Referrals Tab */}
          <TabsContent value="referrals">
            <Card>
              <CardHeader>
                <CardTitle>Insurance Leads</CardTitle>
                <CardDescription>Track quote requests and conversions</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Vehicle Value</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Lead Fee</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referrals.slice(0, 20).map(ref => (
                      <TableRow key={ref.id}>
                        <TableCell>
                          <p className="font-medium">{ref.buyer_email}</p>
                        </TableCell>
                        <TableCell>{ref.provider_name}</TableCell>
                        <TableCell>Rs. {ref.vehicle_value?.toLocaleString()}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{ref.status}</Badge>
                        </TableCell>
                        <TableCell>${ref.lead_price || 25}</TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {new Date(ref.created_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                    {referrals.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                          No leads yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Module Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Insurance Module</Label>
                      <p className="text-sm text-gray-500">Enable/disable entire module</p>
                    </div>
                    <Switch
                      checked={config.module_enabled}
                      onCheckedChange={(v) => setConfig(prev => ({ ...prev, module_enabled: v }))}
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Categories/Badges</Label>
                      <p className="text-sm text-gray-500">Show filter badges</p>
                    </div>
                    <Switch
                      checked={config.categories_enabled}
                      onCheckedChange={(v) => setConfig(prev => ({ ...prev, categories_enabled: v }))}
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Eligibility Matching</Label>
                      <p className="text-sm text-gray-500">AI-powered provider scoring</p>
                    </div>
                    <Switch
                      checked={config.eligibility_matching_enabled}
                      onCheckedChange={(v) => setConfig(prev => ({ ...prev, eligibility_matching_enabled: v }))}
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Insurer Portal</Label>
                      <p className="text-sm text-gray-500">Allow insurer registration</p>
                    </div>
                    <Switch
                      checked={config.insurer_portal_enabled}
                      onCheckedChange={(v) => setConfig(prev => ({ ...prev, insurer_portal_enabled: v }))}
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Sponsored Placements</Label>
                      <p className="text-sm text-gray-500">Allow paid top positions</p>
                    </div>
                    <Switch
                      checked={config.sponsored_placements_enabled}
                      onCheckedChange={(v) => setConfig(prev => ({ ...prev, sponsored_placements_enabled: v }))}
                    />
                  </div>
                  <div>
                    <Label>Max Sponsored Slots</Label>
                    <Input
                      type="number"
                      value={config.sponsored_placements_max || 2}
                      onChange={(e) => setConfig(prev => ({ ...prev, sponsored_placements_max: parseInt(e.target.value) }))}
                      className="w-24 mt-1"
                    />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Notification Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Notify buyer on submit</Label>
                    </div>
                    <Switch
                      checked={config.notify_buyer_on_submit}
                      onCheckedChange={(v) => setConfig(prev => ({ ...prev, notify_buyer_on_submit: v }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Notify buyer on quote</Label>
                    </div>
                    <Switch
                      checked={config.notify_buyer_on_quote}
                      onCheckedChange={(v) => setConfig(prev => ({ ...prev, notify_buyer_on_quote: v }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Notify buyer on policy</Label>
                    </div>
                    <Switch
                      checked={config.notify_buyer_on_policy}
                      onCheckedChange={(v) => setConfig(prev => ({ ...prev, notify_buyer_on_policy: v }))}
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Notify insurer on new lead</Label>
                    </div>
                    <Switch
                      checked={config.notify_insurer_on_new_lead}
                      onCheckedChange={(v) => setConfig(prev => ({ ...prev, notify_insurer_on_new_lead: v }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Notify admin on new insurer</Label>
                    </div>
                    <Switch
                      checked={config.notify_admin_new_insurer}
                      onCheckedChange={(v) => setConfig(prev => ({ ...prev, notify_admin_new_insurer: v }))}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="mt-4">
              <Button onClick={handleSaveConfig} className="bg-indigo-600 hover:bg-indigo-700">
                Save All Settings
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Verification Action Dialog */}
      <Dialog open={showVerificationDialog} onOpenChange={setShowVerificationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {verificationAction === 'approve' && '✅ Approve Insurer'}
              {verificationAction === 'reject' && '❌ Reject Insurer'}
              {verificationAction === 'request_info' && '📝 Request More Information'}
            </DialogTitle>
            <DialogDescription>
              {selectedInsurer?.legal_name} - Score: {selectedInsurer?.verification?.risk_score}/100
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Notes / Reason</Label>
            <Textarea
              placeholder={
                verificationAction === 'approve' ? 'Optional approval notes...' :
                verificationAction === 'reject' ? 'Reason for rejection (required)...' :
                'What information do you need?'
              }
              value={verificationNotes}
              onChange={(e) => setVerificationNotes(e.target.value)}
              rows={3}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVerificationDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleVerificationAction}
              disabled={processingAction || (verificationAction === 'reject' && !verificationNotes)}
              className={
                verificationAction === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                verificationAction === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                'bg-blue-600 hover:bg-blue-700'
              }
            >
              {processingAction ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                verificationAction === 'approve' ? 'Approve' :
                verificationAction === 'reject' ? 'Reject' : 'Send Request'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InsuranceAdminDashboard;
